/**
 * Custom REST data provider for the AOS worker API.
 *
 * Translates Refine's data-layer calls into the worker's REST conventions
 * (IMPLEMENTATION_CONTRACT §5):
 *   - list  → `GET /api/{resource}?page&pageSize&sort&order&q&<field>=<value>`,
 *             response `{ items, total }`.
 *   - getOne→ `GET /api/{resource}/:id`  → bare record.
 *   - create→ `POST /api/{resource}`     → 201 bare record.
 *   - update→ `PATCH /api/{resource}/:id`→ 200 bare record.
 *   - delete→ `DELETE /api/{resource}/:id` → `{ id }`.
 *
 * The Refine resource `name` is used verbatim as the API path segment (they are
 * intentionally identical kebab strings, e.g. `key-results`). All HTTP goes
 * through the shared {@link api} client (`credentials: include`, error
 * normalization).
 */

import type {
  BaseRecord,
  CreateParams,
  CreateResponse,
  CrudFilter,
  CrudSort,
  DataProvider,
  DeleteOneParams,
  DeleteOneResponse,
  GetListParams,
  GetListResponse,
  GetOneParams,
  GetOneResponse,
  LogicalFilter,
  Pagination,
  UpdateParams,
  UpdateResponse,
} from '@refinedev/core';

import { API_URL, api } from '@/lib/api';

/** Worker list-response envelope (CONTRACT §5). */
interface ListEnvelope<T> {
  items: T[];
  total: number;
}

/** Default pagination matching the worker (`page` 1-based, `pageSize` 25). */
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT = 'created_at';
const DEFAULT_ORDER: 'asc' | 'desc' = 'desc';

const RESOURCE_API_PATHS: Record<string, string> = {
  users: 'admin/users',
  roles: 'admin/roles',
};

function resourcePath(resource: string): string {
  return RESOURCE_API_PATHS[resource] ?? resource;
}

/** Type guard: a logical (field/operator/value) filter, not a logical group. */
function isLogicalFilter(filter: CrudFilter): filter is LogicalFilter {
  return (
    (filter as LogicalFilter).field !== undefined &&
    filter.operator !== 'and' &&
    filter.operator !== 'or'
  );
}

/**
 * Map Refine sorters/filters/pagination onto the worker's flat query params.
 *
 * The worker treats any unknown `<field>=<value>` pair as an equality filter and
 * `q` as free-text search, so we project:
 *   - the special field `q` → `q`.
 *   - every other `eq` logical filter → `<field>=<value>`.
 *   - `contains` filters → the worker's `q` search.
 *   - the first sorter → `sort` + `order`.
 * Conditional (and/or) groups and unsupported operators are ignored — the worker
 * ignores unknown columns rather than erroring, keeping behaviour predictable.
 */
function buildListQuery(
  pagination: Pagination | undefined,
  sorters: CrudSort[] | undefined,
  filters: CrudFilter[] | undefined,
): Record<string, string | number> {
  const query: Record<string, string | number> = {
    page: pagination?.current ?? DEFAULT_PAGE,
    pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
  };

  const sorter = sorters?.[0];
  query.sort = sorter?.field ?? DEFAULT_SORT;
  query.order = sorter?.order ?? DEFAULT_ORDER;

  for (const filter of filters ?? []) {
    if (!isLogicalFilter(filter)) continue;
    const { field, operator, value } = filter;
    if (value === undefined || value === null) continue;

    if (field === 'q') {
      query.q = String(value);
      continue;
    }

    if (operator === 'eq') {
      query[field] = typeof value === 'number' ? value : String(value);
    } else if (operator === 'contains' || operator === 'containss') {
      // Treat free-text "contains" as the worker's `q` search.
      query.q = String(value);
    }
    // Other operators (gt/lt/in/...) are not supported by the v0.1 worker; skip.
  }

  return query;
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async <TData extends BaseRecord = BaseRecord>(
    params: GetListParams,
  ): Promise<GetListResponse<TData>> => {
    const { resource, pagination, sorters, filters, meta } = params;
    const query = buildListQuery(pagination, sorters, filters);
    const envelope = await api.get<ListEnvelope<TData>>(`/${resourcePath(resource)}`, {
      query,
      signal: meta?.signal as AbortSignal | undefined,
    });
    return { data: envelope.items, total: envelope.total };
  },

  getOne: async <TData extends BaseRecord = BaseRecord>(
    params: GetOneParams,
  ): Promise<GetOneResponse<TData>> => {
    const { resource, id, meta } = params;
    const record = await api.get<TData>(`/${resourcePath(resource)}/${id}`, {
      signal: meta?.signal as AbortSignal | undefined,
    });
    return { data: record };
  },

  create: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
    params: CreateParams<TVariables>,
  ): Promise<CreateResponse<TData>> => {
    const { resource, variables } = params;
    const record = await api.post<TData>(`/${resourcePath(resource)}`, variables);
    return { data: record };
  },

  update: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
    params: UpdateParams<TVariables>,
  ): Promise<UpdateResponse<TData>> => {
    const { resource, id, variables } = params;
    const record = await api.patch<TData>(`/${resourcePath(resource)}/${id}`, variables);
    return { data: record };
  },

  deleteOne: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
    params: DeleteOneParams<TVariables>,
  ): Promise<DeleteOneResponse<TData>> => {
    const { resource, id } = params;
    const record = await api.delete<TData>(`/${resourcePath(resource)}/${id}`);
    return { data: record };
  },
};

export default dataProvider;
